import moment from "moment";
import AttachmentTaskType from "../../enum/AttachmentTaskType";
import TaskPostServiceStatus from "../../enum/TaskPostServiceStatus";
import Audit from "../../model/Audit";
import Component from "../../model/Component";
import NameEntity from "../../model/NameEntity";
import { formatCurrency } from "../../utils";
import { insertBetween, snakeToCamel } from "../../utils";
import EntityName from "../EntityName";
import ComponentTechnicalAssistance from "../../model/ComponentTechnicalAssistance";
import OcurrenceVisibilityStatus from "../../enum/OcurrenceVisibilityStatus";
import OcurrenceGeneralStatus from "../../enum/OcurrenceGeneralStatus";
import DocumentVersionAttachmentType from "../../enum/DocumentVersionAttachmentType";
import DocumentVersionStatus from "../../enum/DocumentVersionStatus";
import ProductionNonConformityStatus from "../../enum/ProductionNonConformityStatus";
import { FeedbackImproveItem } from "../../pages/System/Task/Tasks/TaskForm";
import OcurrenceClassification from "../../enum/OcurrenceClassification";
import OcurrenceActionPlanStatus from "../../enum/OcurrenceActionPlanStatus";

export interface AuditLabelValueProps {
    audit: Audit;
    fieldName: string;
    value: any;
}

export default function AuditLabelValue(props: AuditLabelValueProps) {
    const { audit, fieldName, value } = props;
    const { controller, action } = audit;
    if (Array.isArray(value) && ['products', 'related_problems', 'damaged_circuit_blocks', 'customer_contacts', 'observers', 'tags', 'composite_productions', 'users'].includes(fieldName)) {
        let camelEntity = '';
        if (fieldName === 'products') {
            camelEntity = 'product';
        } else if (fieldName === 'related_problems') {
            camelEntity = 'relatedProblem';
        } else if (fieldName === 'damaged_circuit_blocks') {
            camelEntity = 'damagedCircuitBlock';
        } else if (fieldName === 'customer_contacts' || fieldName === 'observers' || fieldName === 'users') {
            camelEntity = 'user';
        } else if (fieldName === 'tags') {
            camelEntity = 'tag';
        } else if (fieldName === 'composite_productions') {
            camelEntity = 'production';
        }

        if (camelEntity) {
            const result: React.ReactNode[] = value.map((v, i) => <EntityName<NameEntity>
                key={`${audit.id}_${camelEntity}_${i}`}
                entity={camelEntity}
                entityId={v}
                propMap={i => i.name}
            />);
            return insertBetween(result, ', ');
        }
    } else if (Array.isArray(value) && fieldName === 'references') {
        const result: React.ReactNode[] = value.map((v, i) => <EntityName<NameEntity & { ticket_code: string }>
            key={`${audit.id}_task_${i}`}
            entity={'task'}
            entityId={v}
            propMap={i => `${i.ticket_code} - ${i.name}`}
        />);
        return insertBetween(result, ', ');
    } else if (typeof value === 'boolean' && fieldName !== 'without_component') {
        return value ? 'Sim' : 'Não';
    } else if (fieldName === 'post_office_date' && value) {
        return moment(value).format('DD/MM/YYYY');
    } else if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(value)) {
        return moment(value).format('DD/MM/YYYY HH:mm:ss');
    } else if (fieldName.slice(-3) === '_id') {
        let camelEntity = snakeToCamel(fieldName.replace('_id', ''));
        if (fieldName === 'recurrence_ticket_status_id') {
            camelEntity = 'ticketStatus';
        } else if (fieldName === 'product_review_1_id' || fieldName === 'product_review_2_id') {
            camelEntity = 'productReview';
        } else if (fieldName === 'product_batch_1_id' || fieldName === 'product_batch_2_id') {
            camelEntity = 'productBatch';
        } else if (fieldName === 'approved_by_user_id' || fieldName === 'created_by_user_id' || fieldName === 'evidence_responsible_user_id' || fieldName === 'act_plan_responsible_user_id') {
            camelEntity = 'user';
        } else if (fieldName === 'evidence_ocurrence_id') {
            camelEntity = 'ocurrence';
        }
        
        return <EntityName<NameEntity>
            key={`${audit.id}_${camelEntity}`}
            entity={camelEntity}
            entityId={value}
            propMap={i => i.name || value}
            cache
        />;
    } else if (controller === 'productionNonConformity') {
        if (fieldName === 'status') {
            if (value === ProductionNonConformityStatus.Untreated) {
                return 'Não tratado';
            } else if (value === ProductionNonConformityStatus.ImpossibleToResolve) {
                return 'Impossível de resolver';
            } else if (value === ProductionNonConformityStatus.NotWorthSolving) {
                return 'Não vale a pena resolver';
            } else if (value === ProductionNonConformityStatus.Resolved) {
                return 'Resolvido';
            }
        } else if (fieldName === 'without_component') {
            if (value === true) {
                return 'Não';
            } else if (value === false) {
                return 'Sim';
            }
        } else if (fieldName === 'production_non_conformity_components') {
            if (Array.isArray(value) && value.length > 0) {
                const dados: React.ReactNode[] = value.map((v, i) => {
                    if (typeof v === 'string') {
                        try {
                            const componentData = JSON.parse(v);
                            return (
                                <span key={`${audit.id}_pncc_component_${i}`}>
                                    <EntityName<Component>
                                        entity={'component'}
                                        entityId={String(componentData.component_id)}
                                        propMap={(component) => {
                                            return (component.code ? component.code + ' - ' : '') + component.name;
                                        }}
                                    />
                                    {componentData.amount && <span>, Quantidade: {componentData.amount}</span>}
                                </span>
                            );
                        } catch (e) {
                            return <span key={`${audit.id}_pncc_component_${i}`}>{v}</span>;
                        }
                    }

                    return <span key={`${audit.id}_pncc_component_${i}`}>{String(v)}</span>;
                });

                return insertBetween(dados, <br />);
            }
            return '';
        }
    } else if (controller === 'production') {
        if (fieldName === 'status') {
            if (value === 0) {
                return 'Aberto';
            } else if (value === 1) {
                return 'Concluído';
            }
        }
    } else if (controller === 'technicalAssistance') {
        if (['firmware_updated', 'factory_reset'].includes(fieldName)) {
            if (value === 0) {
                return 'N/A';
            } else if (value === 1) {
                return 'Sim';
            } else if (value === 2) {
                return 'Não';
            }
        } else if (['cost_amount', 'cost_freight', 'cost_materials'].includes(fieldName)) {
            if (value) {
                return formatCurrency(value);
            }
        } else if (fieldName === 'components' && Array.isArray(value)) {
            const result: React.ReactNode[] = value.map((v, i) => {
                //if v is numeric
                if (typeof v === 'number') {
                    return <EntityName<NameEntity>
                        key={`${audit.id}_component_${i}`}
                        entity={'component'}
                        entityId={String(v)}
                        propMap={i => i.name}
                    />;
                }
                let dados = JSON.parse(v) as ComponentTechnicalAssistance;
                return <>
                    <EntityName<Component>
                        key={`${audit.id}_component_${i}`}
                        entity={'component'}
                        entityId={String(dados.component_id)}
                        propMap={(i) => {
                            return (i.code ? i.code + ' - ' : '') + i.name;
                        }}
                    />, <span>Quantidade: {dados.amount || 0}</span>, <span>Valor de venda: {dados.sale_value ? formatCurrency(dados.sale_value) : 'N/A'}</span>,
                    <span>Valor de compra: {dados.purchase_value ? formatCurrency(dados.purchase_value) : 'N/A'}</span>
                </>;
            });
            return insertBetween(result, <br />);
        }
    } else if (controller === 'task') {
        if (['software_improvement_needed', 'hardware_improvement_needed', 'documentation_improvement_needed'].includes(fieldName)) {
            if (value === 0) {
                return 'N/A';
            } else if (value === 1) {
                return 'Sim';
            } else if (value === 2) {
                return 'Não';
            }
        } else if (fieldName === 'post_service_status') {
            if (value === TaskPostServiceStatus.Concluded) {
                return 'Concluído';
            } else if (value === TaskPostServiceStatus.NotAnalyzed) {
                return 'Não analisado';
            } else if (value === TaskPostServiceStatus.Pending) {
                return 'Pendente';
            } else if (value === TaskPostServiceStatus.Approved) {
                return 'Aprovado';
            }
        } else if (fieldName === 'type' && ['saveAttachment', 'deleteAttachment', 'updateAttachment'].includes(action)) {
            if (value === AttachmentTaskType.Analysis) {
                return 'Análise';
            } else if (value === AttachmentTaskType.Client) {
                return 'Cliente';
            } else if (value === AttachmentTaskType.Solution) {
                return 'Solução';
            } else if (value === AttachmentTaskType.PostService) {
                return 'Pós-Atendimento';
            }
        } else if (fieldName === 'description') {
            return <div
                className="mudarCorA"
                dangerouslySetInnerHTML={{ __html: value }}
            />;
        } else if (fieldName === 'feedback_to_improve_item') {
            if (value === FeedbackImproveItem.Hardware) {
                return 'Hardware';
            } else if (value === FeedbackImproveItem.Software) {
                return 'Software';
            } else if (value === FeedbackImproveItem.Documentation) {
                return 'Documentação';
            }
        }
    } else if (controller === 'orderReceived') {
        if (fieldName === 'invoice_status') {
            if (value === 0) {
                return 'Visível';
            } else if (value === 1) {
                return 'Não tem';
            } else if (value === 2) {
                return 'Lacrada';
            }
        }
    } else if (controller === 'ocurrence') {
        if (fieldName === 'visibility_status') {
            if (value === OcurrenceVisibilityStatus.Private) {
                return 'Privada';
            } else if (value === OcurrenceVisibilityStatus.PublicSector) {
                return 'Pública para o setor';
            } else if (value === OcurrenceVisibilityStatus.PublicAll) {
                return 'Pública para toda empresa';
            }
        } else if (fieldName === 'general_status') {
            if (value === OcurrenceGeneralStatus.Registered) {
                return 'Registrada';
            } else if (value === OcurrenceGeneralStatus.UnderAnalysis) {
                return 'Em análise';
            } else if (value === OcurrenceGeneralStatus.Completed) {
                return 'Concluída';
            }
        } else if (fieldName === 'impact') {
            if (value === 0) {
                return 'Nenhum';
            } else if (value === 1) {
                return 'Baixo';
            } else if (value === 2) {
                return 'Médio';
            } else if (value === 3) {
                return 'Alto';
            }
        } else if (fieldName === 'require_action_record') {
            if (value === true) {
                return 'Sim';
            } else if (value === false) {
                return 'Não';
            }
        } else if (fieldName === 'classification') {
            if (value === OcurrenceClassification.Correction) {
                return 'Correção';
            } else if (value === OcurrenceClassification.Prevention) {
                return 'Prevenção';
            } else if (value === OcurrenceClassification.Improvement) {
                return 'Melhoria';
            }
        }
    } else if(controller === 'ocurrenceActionPlan') {
        if (fieldName === 'status') {
            if (value === OcurrenceActionPlanStatus.InProgress) {
                return 'Em andamento';
            } else if (value === OcurrenceActionPlanStatus.Closed) {
                return 'Fechado';
            } else if(value === OcurrenceActionPlanStatus.Open) {
                return 'Aberto';
            }
        } else if (fieldName === 'action_plan_date' || fieldName === 'evidence_plan_date') {
            if (value) {
                return moment(value).format('DD/MM/YYYY');
            }
        } else if (fieldName === 'effective_action') {
            if (value === 1) {
                return 'Não';
            } else if (value === 2) {
                return 'Sim';
            }
        }
    } else if (controller === 'documentVersion') {
        if (fieldName === 'attachment_type') {
            if (value === DocumentVersionAttachmentType.File) {
                return 'Arquivo';
            } else if (value === DocumentVersionAttachmentType.Link) {
                return 'Link';
            }
        } else if (fieldName === 'status') {
            if (value === DocumentVersionStatus.Approved) {
                return 'Aprovado';
            } else if (value === DocumentVersionStatus.AwaitingApproval) {
                return 'Aguardando aprovação';
            } else if (value === DocumentVersionStatus.Rejected) {
                return 'Reprovado';
            } else if (value === DocumentVersionStatus.InProgress) {
                return 'Em andamento';
            }
        }
    }
    return value;
}